import { winnerContract } from '@daymade/api-contracts';
import { PrizeType } from '@daymade/common-lib/dbEnums';
import { ClientInferResponseBody } from '@ts-rest/core';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import PrizeOption from 'components/prizes/PrizeOption';
import SecondaryImagesContainer from 'components/prizes/SecondaryImagesContainer';
import RoundedButton from 'components/RoundedButton';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import Link from 'next/link';
import { useState } from 'react';
import { promoApiClient, winnerApiClient } from 'utils/apiClient';
import { ASSET_PREFIX } from 'utils/constants';
import HttpStatusCode from 'utils/HttpStatusCode';
import { AddressForm, TAddress } from './AddressForm';

type TSelectedPrize = ClientInferResponseBody<
  typeof winnerContract.auth.getPrizeBySlugForRedemption,
  200
>;

interface TChoosePrizeModalProps {
  selectedPrize: TSelectedPrize;
  sourceType: 'prizeWin' | 'promoPrizeReward';
  sourceId: string;
  isChosenByPlayer?: boolean;
  closePopup: () => void;
  onSuccessfulChoiceHandler: () => Promise<void>;
}

const ChoosePrizeModal = observer(
  ({
    selectedPrize,
    sourceType,
    sourceId,
    isChosenByPlayer = true,
    closePopup,
    onSuccessfulChoiceHandler,
  }: TChoosePrizeModalProps) => {
    const { displayToast } = useUIStore();
    const { balances, updateTokenBalance } = useUserStore();

    // Error message for prize confirm
    const [prizeChoiceError, setPrizeChoiceError] = useState<string | null>(
      null,
    );

    const [confirmLoading, setConfirmLoading] = useState(false);

    const [selectedOption, setSelectedOption] = useState<
      TSelectedPrize['products'][number] | null
    >(
      selectedPrize.type === PrizeType.OPTION
        ? selectedPrize.products[0]
        : null,
    );

    const [deliveryAddress, setDeliveryAddress] = useState<TAddress>();
    const [emailAddress, setEmailAddress] = useState<string>();
    const [phoneNumber, setPhoneNumber] = useState<string>();
    const [additionalQuestionsAnswers, setAdditionalQuestionsAnswers] =
      useState<TSelectedPrize['additionalQuestionsForPlayer']>(
        Object.fromEntries(
          Object.keys(selectedPrize.additionalQuestionsForPlayer).map((key) => [
            key,
            '',
          ]),
        ),
      );

    const [confirmingPrizeChoice, setConfirmingPrizeChoice] = useState(false);

    // Used to show additional info after submitting prize choice
    const codesRequiringPostSubmitInfo = ['GIFTCLOUD_REWARD', 'DAYMADE_TOKEN']; // prize product auto-fulfilment codes
    const [postSubmitInfo, setPostSubmitInfo] = useState<string | null>(null);

    const handleDismiss = () => {
      if (postSubmitInfo) {
        // This path is only executed if we need to display additional info.
        onSuccessfulChoiceHandler();
      } else {
        closePopup();
      }
    };

    const handleConfirmPrize = async () => {
      setConfirmLoading(true);
      setPrizeChoiceError(null);

      if (
        [
          ...(selectedPrize.requiresDeliveryAddress
            ? Object.values(deliveryAddress!)
            : []),
          ...(selectedPrize.requiresEmailAddress ? [emailAddress] : []),
          ...(selectedPrize.requiresPhoneNumber ? [phoneNumber] : []),
          ...Object.values(additionalQuestionsAnswers),
        ].some((v) => v === undefined || v.trim() === '')
      ) {
        setPrizeChoiceError(
          'Please fill out all fields before submitting your choice.',
        );
        setConfirmLoading(false);
        return;
      }

      const resp =
        sourceType === 'prizeWin'
          ? await winnerApiClient.auth.submitPrizeChoice({
              body: {
                prizeWinId: sourceId,
                prizeId: selectedPrize.id,
                productId: selectedOption?.id,
                deliveryStreet: deliveryAddress?.street,
                deliveryCity: deliveryAddress?.city,
                deliveryPostcode: deliveryAddress?.postcode,
                emailAddress,
                phoneNumber,
                additionalInfoFromPlayer: additionalQuestionsAnswers,
              },
            })
          : await promoApiClient.auth.claimPromoPrizeReward({
              params: {
                id: sourceId,
              },
              body: {
                prizeId: selectedPrize.id,
                productId: selectedOption?.id,
                deliveryStreet: deliveryAddress?.street,
                deliveryCity: deliveryAddress?.city,
                deliveryPostcode: deliveryAddress?.postcode,
                emailAddress,
                phoneNumber,
                additionalInfoFromPlayer: additionalQuestionsAnswers,
              },
            });

      switch (resp.status) {
        case HttpStatusCode.OK: {
          const postSubmitInfoCode = selectedPrize.products.find(
            ({ autoFulfilmentCode }) =>
              autoFulfilmentCode &&
              codesRequiringPostSubmitInfo.includes(autoFulfilmentCode),
          )?.autoFulfilmentCode;

          if (postSubmitInfoCode) {
            if (postSubmitInfoCode === 'DAYMADE_TOKEN') {
              await updateTokenBalance();
            }
            setPostSubmitInfo(postSubmitInfoCode);
          } else {
            await onSuccessfulChoiceHandler();
            displayToast({
              title: `${isChosenByPlayer ? 'Great choice! ' : ''}We'll be in touch via email within 14 working days to get you your prize.`,
              timeout: 9000,
            });
          }

          break;
        }
        case HttpStatusCode.BAD_REQUEST:
        case HttpStatusCode.UNAUTHORIZED:
          handleDismiss();
          displayToast({
            title: resp.body.message,
            color: '#FF6600',
            timeout: 5000,
          });
          break;
      }

      setConfirmLoading(false);
    };

    const handleChoosePrize = async () => {
      setConfirmingPrizeChoice(true);
    };

    return (
      <div
        className="popupWithDimmedBackground container"
        id="dimmedBackground"
        onClick={(e) => {
          // Asserting the target as an HTMLElement
          const target = e.target as HTMLElement;

          if (target.id === 'dimmedBackground') {
            handleDismiss();
          }
        }}
      >
        {confirmingPrizeChoice ? (
          <div className="message" id="choosePrizeModal">
            <img
              id="closeButton"
              src={`${ASSET_PREFIX}/images/close_button.svg`}
              style={{ cursor: 'pointer', width: '15px' }}
              onClick={handleDismiss}
              alt="close button - cross"
            />

            <img
              src={
                selectedOption?.imageLocation ??
                selectedPrize.imageLocations.main
              }
              className="thumbnail"
              style={{
                borderRadius: '5px',
                marginTop: '2em',
                maxWidth: '250px',
                width: '100%',
              }}
            />

            <p
              style={{
                fontStyle: 'italic',
                fontWeight: 'bold',
                margin: '1em',
                fontSize: '1.2em',
              }}
            >
              You've {isChosenByPlayer ? 'chosen' : 'won'}
            </p>

            <div className="separator" style={{ margin: 0 }} />

            <p style={{ fontWeight: '900', margin: '1em', fontSize: '1.3em' }}>
              {selectedPrize.title}{' '}
              {selectedOption?.name && `(${selectedOption.name})`}
            </p>

            <div className="separator" style={{ margin: 0 }} />

            {selectedPrize.requiresDeliveryAddress && (
              <>
                <h5 style={{ margin: '1.2em 0.5em 0.6em 0.5em' }}>
                  Delivery Address
                </h5>
                <p>
                  Please enter the address where you would like your prize
                  delivered
                </p>
                <AddressForm
                  onChange={(address) => {
                    setPrizeChoiceError(null);
                    setDeliveryAddress(address);
                  }}
                />
              </>
            )}

            {selectedPrize.requiresEmailAddress && (
              <>
                <h5 style={{ margin: '1.2em 0.5em 0.6em 0.5em' }}>
                  Email Address
                </h5>
                <p>
                  Please enter your preferred email address to receive your
                  prize
                </p>
                <input
                  type="text"
                  required
                  placeholder="Type here.."
                  className="inputTextBox"
                  value={emailAddress}
                  onChange={(e) => {
                    setPrizeChoiceError(null);
                    setEmailAddress(e.target.value);
                  }}
                />
              </>
            )}

            {selectedPrize.requiresPhoneNumber && (
              <>
                <h5 style={{ margin: '1.2em 0.5em 0.6em 0.5em' }}>
                  Phone Number
                </h5>
                <p>Please enter your preferred number for delivery updates</p>
                <input
                  type="text"
                  placeholder="Type here.."
                  className="inputTextBox"
                  value={phoneNumber}
                  onChange={(e) => {
                    setPrizeChoiceError(null);
                    setPhoneNumber(e.target.value);
                  }}
                />
              </>
            )}

            {Object.entries(selectedPrize.additionalQuestionsForPlayer).map(
              ([question, description]) => (
                <>
                  <h5 style={{ margin: '1.2em 0.5em 0.6em 0.5em' }}>
                    {question}
                  </h5>

                  <p>{description}</p>

                  <input
                    type="text"
                    required
                    placeholder="Type here.."
                    className="inputTextBox"
                    value={additionalQuestionsAnswers[question]}
                    onChange={(e) => {
                      setPrizeChoiceError(null);
                      setAdditionalQuestionsAnswers((prev) => ({
                        ...prev,
                        [question]: e.target.value,
                      }));
                    }}
                  />
                </>
              ),
            )}

            {prizeChoiceError && (
              <div
                style={{
                  width: '90%',
                  fontWeight: 700,
                  color: 'red',
                  marginTop: 10,
                  textAlign: 'left',
                }}
              >
                {prizeChoiceError}
              </div>
            )}

            {postSubmitInfo ? (
              <div
                style={{
                  margin: '2em',
                  textAlign: 'center',
                  fontSize: '1.2em',
                }}
              >
                {postSubmitInfo === 'GIFTCLOUD_REWARD' ? (
                  <p>
                    Your gift card should be with you shortly! Please check your
                    email to find the link to redeem your gift card! ❤️
                  </p>
                ) : postSubmitInfo === 'DAYMADE_TOKEN' ? (
                  <>
                    <p>
                      You have successfully redeemed your prize! You now have{' '}
                      <strong>{balances?.tokenBalance} tokens</strong>!
                    </p>
                    <Link href="/account?action=redeem-tokens">
                      <a style={{ fontSize: '1.1em' }}>
                        View Redemption Options →
                      </a>
                    </Link>
                  </>
                ) : null}
              </div>
            ) : (
              <RoundedButton
                onClick={handleConfirmPrize}
                disabled={
                  selectedPrize.type === PrizeType.OPTION && !selectedOption
                }
                className="chooseButton"
                loading={confirmLoading}
                style={{ width: '90%' }}
              >
                CONFIRM{isChosenByPlayer ? ' CHOICE' : ''}
              </RoundedButton>
            )}

            <p
              className="prizeFulfillmentText"
              style={{ padding: '0 1em 1em 1em', fontStyle: 'italic' }}
            >
              By confirming{isChosenByPlayer ? ' your prize choice' : ''} you
              agree to us sharing your details with our prize fulfillment
              partner who will send and organise delivery of your prize.
            </p>
          </div>
        ) : (
          <div className="message" id="choosePrizeModal">
            <div className="heroSection">
              <img
                id="closeButton"
                src={`${ASSET_PREFIX}/images/close_button_white.svg`}
                style={{ cursor: 'pointer', width: '15px' }}
                onClick={handleDismiss}
                alt="close button - cross"
              />

              <h3 style={{ marginBottom: 0 }}>
                <strong>{selectedPrize.title}</strong>
              </h3>
              <h4 style={{ marginTop: 0 }}>{selectedPrize.subheading}</h4>

              <div
                className="blurb"
                dangerouslySetInnerHTML={{
                  __html: selectedPrize.overviewBody || '',
                }}
              />

              {/* {selectedPrize.winningCriteria[MAIN_DRAW_SCHEDULE_NAME] && ( // TBC
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    marginTop: '1em',
                  }}
                >
                  <PrizeTierBadge
                    prizeTier={
                      selectedPrize.winningCriteria[MAIN_DRAW_SCHEDULE_NAME].tier
                    }
                  />
                </div>
              )} */}
            </div>

            {selectedPrize.whatsIncludedBody && (
              <PaddedMaxWidthContainer>
                <p
                  style={{
                    margin: '1em 0',
                    fontSize: '1.6rem',
                    fontWeight: 800,
                  }}
                >
                  {selectedPrize.whatsIncludedHeading || "What's Included?"}
                </p>

                <div
                  style={{
                    marginTop: '1em',
                    fontSize: '1.25em',
                    margin: '1em',
                    textAlign: 'left',
                    width: '100%',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: selectedPrize.whatsIncludedBody,
                  }}
                />

                {(selectedPrize.type === PrizeType.BUNDLE ||
                  selectedPrize.type === PrizeType.MULTI_OPTION) && (
                  <ul>
                    {selectedPrize.products.map((product, i) => (
                      <li key={i}>{product.name}</li>
                    ))}
                  </ul>
                )}
              </PaddedMaxWidthContainer>
            )}

            <div className="paddedMaxWidthContainer">
              <SecondaryImagesContainer
                image1Location={selectedPrize.imageLocations.secondary1}
                image2Location={selectedPrize.imageLocations.secondary2}
                image1AltText={undefined}
                image2AltText={undefined}
              />

              {selectedPrize.type === PrizeType.OPTION && (
                <>
                  <h5 style={{ width: '100%', margin: '2em 0 1em 0' }}>
                    Select An Option
                  </h5>
                  {selectedPrize.products.map((product, i) => (
                    <PrizeOption
                      product={product}
                      key={i}
                      onClick={() => {
                        setSelectedOption(product);
                      }}
                      selected={selectedOption?.id === product.id}
                    />
                  ))}
                </>
              )}

              <RoundedButton
                onClick={handleChoosePrize}
                disabled={
                  selectedPrize.type === PrizeType.OPTION && !selectedOption
                }
                className="chooseButton"
              >
                {isChosenByPlayer ? 'CHOOSE' : 'CLAIM'}{' '}
                {selectedPrize.type === PrizeType.OPTION && selectedOption
                  ? selectedOption.name.toUpperCase()
                  : 'PRIZE'}
              </RoundedButton>
            </div>
          </div>
        )}

        <style jsx>{`
          .container {
            position: fixed;
            top: 0;
            left: 0;
            max-width: 100%;
            height: 100%;
            background-color: rgba(55, 55, 55, 0.75);
            z-index: 999;
          }

          #closeButton {
            position: fixed;
            width: 20px;
            height: 20px;
            right: 16px;
            top: 16px;
          }

          .message {
            position: fixed;
            top: 50%;
            left: 50%;
            width: 95%;
            max-width: 600px;
            max-height: 95%;
            overflow-y: auto;
            -webkit-transform: translate(-50%, -50%);
            transform: translate(-50%, -50%);

            display: flex;
            flex-direction: column;
            align-items: center;
            border-radius: 5px;
            box-shadow: 2px 2px 12px rgba(55, 55, 55, 0.6);
            background-color: white;
            z-index: 1000;
          }

          :global(#choosePrizeModal *) {
            flex-shrink: 0;
          }

          h5 {
            text-transform: none;
            letter-spacing: normal;
          }

          .heroSection {
            background-image: url(${selectedPrize.imageLocations.main});
            background-size: cover;
            background-position: center;
            width: 100%;
            min-height: 110px;
            box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.6);
            padding: 2em;
            display: flex;
            flex-direction: column;
          }

          h3,
          h4,
          .blurb {
            color: white;
            text-align: left;
            text-shadow: 0 0 6px black;
          }

          .blurb {
            font-size: 1.1em;
          }

          .secondaryImage {
            width: 48%;
            height: 100%;
          }

          ul {
            width: 100%;
            padding-left: 2em;
            margin: 0;
            font-weight: 600;
            font-size: 1.1em;
          }

          :global(.chooseButton) {
            position: relative;
            justify-self: center;
            margin: 20px;
            width: 100%;
            white-space: normal !important;
          }

          // This is needed to add padding below the button when scrolling is enabled on the parent div.
          :global(.chooseButton:after) {
            content: '';
            display: block;
            position: absolute;
            bottom: -2rem;
            width: 1px;
            height: 2rem;
          }

          .inputTextBox {
            border-radius: 8px;
            border: 2px solid lightgray;
            min-width: 250px;
            width: 90%;
            padding: 1em;
            margin-left: auto;
            margin-right: auto;
          }

          :global(.prizeFulfillmentText) {
            font-style: italic;
            margin: 0 1em;
          }

          :global(.prizeFulfillmentText:after) {
            content: '';
            display: block;
            position: absolute;
            bottom: -2rem;
            width: 1px;
            height: 2rem;
          }
        `}</style>
      </div>
    );
  },
);

export default ChoosePrizeModal;
